/**
 * Multi-Factor Authentication Service
 * Handles MFA enrollment and verification using Firebase Identity Platform
 * Supports both SMS and TOTP (Authenticator App) methods
 */

import { auth } from '../firebase/config';
import {
  multiFactor,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  TotpMultiFactorGenerator,
  RecaptchaVerifier,
  getMultiFactorResolver,
  reauthenticateWithCredential,
  EmailAuthProvider
} from 'firebase/auth';
import { updateUserProfile } from './userService';

// Store RecaptchaVerifier instance for reuse
let recaptchaVerifier = null;

/**
 * Initialize or get RecaptchaVerifier for SMS operations
 * @param {string} containerId - The DOM element ID for the reCAPTCHA container
 * @returns {Promise<RecaptchaVerifier>}
 */
export const getRecaptchaVerifier = async (containerId = 'recaptcha-container') => {
  // Clear existing verifier if it exists
  if (recaptchaVerifier) {
    try {
      recaptchaVerifier.clear();
    } catch (e) {
      console.log('Could not clear previous reCAPTCHA verifier');
    }
    recaptchaVerifier = null;
  }
  
  // Find the container element
  const container = document.getElementById(containerId);
  if (!container) {
    console.error(`RecaptchaVerifier: Container element '${containerId}' not found in DOM`);
    throw new Error(`reCAPTCHA container element '${containerId}' not found. Please ensure the element exists.`);
  }
  
  // Ensure auth is ready
  if (!auth) {
    throw new Error('Firebase Auth is not initialized');
  }
  
  console.log('Creating RecaptchaVerifier with:', {
    containerId,
    containerExists: !!container,
    authExists: !!auth,
    authCurrentUser: auth.currentUser?.uid
  });
  
  try {
    // Use the global window pattern for RecaptchaVerifier
    // This is the recommended approach from Firebase documentation
    window.recaptchaVerifier = new RecaptchaVerifier(auth, containerId, {
      size: 'invisible',
      callback: (response) => {
        console.log('reCAPTCHA verified, response:', response);
      },
      'expired-callback': () => {
        console.log('reCAPTCHA expired');
        window.recaptchaVerifier = null;
      }
    });
    
    recaptchaVerifier = window.recaptchaVerifier;
    
    // Render the reCAPTCHA widget
    const widgetId = await recaptchaVerifier.render();
    console.log('RecaptchaVerifier rendered successfully, widgetId:', widgetId);
    
    return recaptchaVerifier;
  } catch (error) {
    console.error('Error creating RecaptchaVerifier:', error);
    console.error('Error code:', error.code);
    console.error('Error message:', error.message);
    
    // Provide more helpful error messages
    if (error.code === 'auth/argument-error') {
      throw new Error(
        'Failed to initialize reCAPTCHA. This may be due to: ' +
        '1) SMS Multi-factor Authentication not enabled in Firebase Console, ' +
        '2) Missing reCAPTCHA configuration, or ' +
        '3) Domain not authorized in Firebase Console. ' +
        'Please check your Firebase Authentication settings.'
      );
    }
    
    recaptchaVerifier = null;
    window.recaptchaVerifier = null;
    throw error;
  }
};

/**
 * Clear the RecaptchaVerifier instance
 */
export const clearRecaptchaVerifier = () => {
  if (recaptchaVerifier) {
    try {
      recaptchaVerifier.clear();
    } catch (e) {
      console.log('Could not clear reCAPTCHA verifier');
    }
    recaptchaVerifier = null;
  }
};

/**
 * Get the currently enrolled MFA factors for the authenticated user
 * @returns {Array} Array of enrolled factors with their info
 */
export const getEnrolledFactors = () => {
  const user = auth.currentUser;
  if (!user) {
    return [];
  }
  
  const enrolledFactors = multiFactor(user).enrolledFactors;
  return enrolledFactors.map(factor => ({
    uid: factor.uid,
    factorId: factor.factorId,
    displayName: factor.displayName || 'Unnamed factor',
    enrollmentTime: factor.enrollmentTime,
    // For phone factors, include the phone number hint
    phoneNumber: factor.phoneNumber || null
  }));
};

/**
 * Check if the user has MFA enabled
 * @returns {boolean}
 */
export const isMfaEnabled = () => {
  const factors = getEnrolledFactors();
  return factors.length > 0;
};

/**
 * Check if a specific MFA type is enrolled
 * @param {string} factorId - 'phone' for SMS, 'totp' for authenticator
 * @returns {boolean}
 */
export const isFactorEnrolled = (factorId) => {
  const factors = getEnrolledFactors();
  return factors.some(f => f.factorId === factorId);
};

/**
 * Reauthenticate user before MFA operations (required by Firebase)
 * @param {string} password - User's current password
 * @returns {Promise<void>}
 */
export const reauthenticateUser = async (password) => {
  const user = auth.currentUser;
  if (!user || !user.email) {
    throw new Error('No authenticated user found');
  }
  
  const credential = EmailAuthProvider.credential(user.email, password);
  await reauthenticateWithCredential(user, credential);
};

// =============================================================================
// SMS MFA ENROLLMENT
// =============================================================================

/**
 * Start SMS MFA enrollment - sends verification code to phone
 * @param {string} phoneNumber - Phone number in E.164 format (e.g., +1234567890)
 * @param {string} recaptchaContainerId - DOM element ID for reCAPTCHA
 * @returns {Promise<string>} Verification ID needed to complete enrollment
 */
export const startSmsEnrollment = async (phoneNumber, recaptchaContainerId = 'recaptcha-container') => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('No authenticated user found');
  }
  
  // Get the multi-factor session
  const multiFactorSession = await multiFactor(user).getSession();
  
  // Initialize reCAPTCHA verifier (now async)
  const verifier = await getRecaptchaVerifier(recaptchaContainerId);
  
  // Create phone info options
  const phoneInfoOptions = {
    phoneNumber: phoneNumber,
    session: multiFactorSession
  };
  
  // Send SMS verification code
  const phoneAuthProvider = new PhoneAuthProvider(auth);
  const verificationId = await phoneAuthProvider.verifyPhoneNumber(phoneInfoOptions, verifier);
  
  return verificationId;
};

/**
 * Complete SMS MFA enrollment with verification code
 * @param {string} verificationId - From startSmsEnrollment
 * @param {string} verificationCode - Code received via SMS
 * @param {string} displayName - Optional display name for this factor
 * @returns {Promise<void>}
 */
export const completeSmsEnrollment = async (verificationId, verificationCode, displayName = 'Phone') => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('No authenticated user found');
  }
  
  // Create credential from verification
  const credential = PhoneAuthProvider.credential(verificationId, verificationCode);
  
  // Create multi-factor assertion
  const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(credential);
  
  // Enroll the factor
  await multiFactor(user).enroll(multiFactorAssertion, displayName);
  
  // Update user profile to reflect MFA enabled
  await updateUserProfile(user.uid, {
    profileData: { twoFactorEnabled: true }
  });
  
  // Clear reCAPTCHA
  clearRecaptchaVerifier();
};

// =============================================================================
// TOTP (Authenticator App) MFA ENROLLMENT
// =============================================================================

/**
 * Generate TOTP secret for authenticator app enrollment
 * @returns {Promise<Object>} Object containing secret key and QR code URL
 */
export const generateTotpSecret = async () => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('No authenticated user found');
  }
  
  // Get the multi-factor session
  const multiFactorSession = await multiFactor(user).getSession();
  
  // Generate TOTP secret
  const totpSecret = await TotpMultiFactorGenerator.generateSecret(multiFactorSession);
  
  // Generate QR code URL for authenticator apps
  // The account name will be the user's email
  const accountName = user.email || 'Healthcare EMR';
  const issuer = 'Healthcare EMR';
  const qrCodeUrl = totpSecret.generateQrCodeUrl(accountName, issuer);
  
  return {
    secret: totpSecret,
    secretKey: totpSecret.secretKey,
    qrCodeUrl: qrCodeUrl
  };
};

/**
 * Complete TOTP MFA enrollment with verification code from authenticator app
 * @param {TotpSecret} totpSecret - Secret from generateTotpSecret
 * @param {string} verificationCode - 6-digit code from authenticator app
 * @param {string} displayName - Optional display name for this factor
 * @returns {Promise<void>}
 */
export const completeTotpEnrollment = async (totpSecret, verificationCode, displayName = 'Authenticator App') => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('No authenticated user found');
  }
  
  // Create multi-factor assertion from TOTP
  const multiFactorAssertion = TotpMultiFactorGenerator.assertionForEnrollment(
    totpSecret,
    verificationCode
  );
  
  // Enroll the factor
  await multiFactor(user).enroll(multiFactorAssertion, displayName);
  
  // Update user profile to reflect MFA enabled
  await updateUserProfile(user.uid, {
    profileData: { twoFactorEnabled: true }
  });
};

// =============================================================================
// UNENROLL MFA
// =============================================================================

/**
 * Remove an enrolled MFA factor
 * @param {string} factorUid - The UID of the factor to remove
 * @returns {Promise<void>}
 */
export const unenrollFactor = async (factorUid) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('No authenticated user found');
  }
  
  // Find the factor to unenroll
  const enrolledFactors = multiFactor(user).enrolledFactors;
  const factorToRemove = enrolledFactors.find(f => f.uid === factorUid);
  
  if (!factorToRemove) {
    throw new Error('Factor not found');
  }
  
  // Unenroll the factor
  await multiFactor(user).unenroll(factorToRemove);
  
  // Check if any factors remain
  const remainingFactors = multiFactor(user).enrolledFactors;
  if (remainingFactors.length === 0) {
    // Update user profile to reflect MFA disabled
    await updateUserProfile(user.uid, {
      profileData: { twoFactorEnabled: false }
    });
  }
};

/**
 * Remove all enrolled MFA factors
 * @returns {Promise<void>}
 */
export const unenrollAllFactors = async () => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('No authenticated user found');
  }
  
  const enrolledFactors = multiFactor(user).enrolledFactors;
  
  // Unenroll each factor
  for (const factor of enrolledFactors) {
    await multiFactor(user).unenroll(factor);
  }
  
  // Update user profile
  await updateUserProfile(user.uid, {
    profileData: { twoFactorEnabled: false }
  });
};

// =============================================================================
// MFA SIGN-IN VERIFICATION
// =============================================================================

/**
 * Get MFA resolver from sign-in error
 * @param {Error} error - The auth/multi-factor-auth-required error
 * @returns {MultiFactorResolver}
 */
export const getMfaResolver = (error) => {
  return getMultiFactorResolver(auth, error);
};

/**
 * Get hints about enrolled factors from resolver
 * @param {MultiFactorResolver} resolver
 * @returns {Array} Array of factor hints
 */
export const getFactorHints = (resolver) => {
  return resolver.hints.map(hint => ({
    uid: hint.uid,
    factorId: hint.factorId,
    displayName: hint.displayName || 'Unknown',
    phoneNumber: hint.phoneNumber || null
  }));
};

/**
 * Start SMS verification during sign-in
 * @param {MultiFactorResolver} resolver - MFA resolver from error
 * @param {number} hintIndex - Index of the phone factor in resolver.hints
 * @param {string} recaptchaContainerId - DOM element ID for reCAPTCHA
 * @returns {Promise<string>} Verification ID
 */
export const startSmsSignInVerification = async (resolver, hintIndex, recaptchaContainerId = 'recaptcha-container') => {
  const hint = resolver.hints[hintIndex];
  
  if (hint.factorId !== PhoneMultiFactorGenerator.FACTOR_ID) {
    throw new Error('Selected factor is not a phone factor');
  }
  
  // Initialize reCAPTCHA verifier (now async)
  const verifier = await getRecaptchaVerifier(recaptchaContainerId);
  
  const phoneInfoOptions = {
    multiFactorHint: hint,
    session: resolver.session
  };
  
  const phoneAuthProvider = new PhoneAuthProvider(auth);
  const verificationId = await phoneAuthProvider.verifyPhoneNumber(phoneInfoOptions, verifier);
  
  return verificationId;
};

/**
 * Complete SMS verification during sign-in
 * @param {MultiFactorResolver} resolver - MFA resolver from error
 * @param {string} verificationId - From startSmsSignInVerification
 * @param {string} verificationCode - Code received via SMS
 * @returns {Promise<UserCredential>}
 */
export const completeSmsSignInVerification = async (resolver, verificationId, verificationCode) => {
  const credential = PhoneAuthProvider.credential(verificationId, verificationCode);
  const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(credential);
  
  const userCredential = await resolver.resolveSignIn(multiFactorAssertion);
  
  // Clear reCAPTCHA
  clearRecaptchaVerifier();
  
  return userCredential;
};

/**
 * Complete TOTP verification during sign-in
 * @param {MultiFactorResolver} resolver - MFA resolver from error
 * @param {number} hintIndex - Index of the TOTP factor in resolver.hints
 * @param {string} verificationCode - 6-digit code from authenticator app
 * @returns {Promise<UserCredential>}
 */
export const completeTotpSignInVerification = async (resolver, hintIndex, verificationCode) => {
  const hint = resolver.hints[hintIndex];
  
  if (hint.factorId !== TotpMultiFactorGenerator.FACTOR_ID) {
    throw new Error('Selected factor is not a TOTP factor');
  }
  
  const multiFactorAssertion = TotpMultiFactorGenerator.assertionForSignIn(
    hint.uid,
    verificationCode
  );
  
  const userCredential = await resolver.resolveSignIn(multiFactorAssertion);
  
  return userCredential;
};

/**
 * Constants for factor IDs
 */
export const FACTOR_IDS = {
  PHONE: PhoneMultiFactorGenerator.FACTOR_ID,
  TOTP: TotpMultiFactorGenerator.FACTOR_ID
};

const mfaService = {
  getRecaptchaVerifier,
  clearRecaptchaVerifier,
  getEnrolledFactors,
  isMfaEnabled,
  isFactorEnrolled,
  reauthenticateUser,
  startSmsEnrollment,
  completeSmsEnrollment,
  generateTotpSecret,
  completeTotpEnrollment,
  unenrollFactor,
  unenrollAllFactors,
  getMfaResolver,
  getFactorHints,
  startSmsSignInVerification,
  completeSmsSignInVerification,
  completeTotpSignInVerification,
  FACTOR_IDS
};

export default mfaService;

